import { createBaseOptions } from '@/components/unlayer/unlayer.util';
import {
    UNLAYER_EMAIL_LINKS_TAG_LIMIT,
    DEFAULT_FROM_VALUE, DEFAULT_TOADDRESS_VALUE,
    EMAIL_BROADCAST_PREVIEW,
    EMAIL_BROADCAST_SUBJECT,
} from '@/components/broadcast/constants/emailBroadcast.constants';
import { STEPS } from '@/components/unlayer/constants/editor.constants';
import { SOURCES } from '@/components/unlayer/constants/unlayer.constants';
import merge from 'lodash/merge'

export const i18n = {
    additionalText: 'Additional footer text',
    alignment: 'Alignment',
    appointmentLinks: 'Appointment links',
    contentSectionHeader: 'Content',
    optOut: 'Opt Out',
    postLinkText: 'Unsubscribe link post-text',
    preLinkText: 'Unsubscribe link pre-text',
    unsubscribe: 'Unsubscribe',
    signature: 'Signature',
    usersSectionHeader: 'User',
    userLabel: 'Use signature for',
    contactOwnerLabel: 'Contact Owner (Automatic)',
    applyTagLabel: 'Apply Tag',
    urlLabel: 'URL',
    openWebsiteLabel: 'Open Website',
    mergeFieldDropdownLabel: 'Merge fields',
    campaignLinkDropdownLabel: 'Automation Links',
    campaignLinkLabel: 'Automation Links',
    tagsLimitReachedLabel: 'You\'ve reached the maximum number of tags.',
    openFileManagement: 'Open file management...',
};

export const createUnlayerEditorOptionsForPreview = ({
    userSignatures,
    users,
    customToolsUri,
}) => ({
    customJS: [
        `window.signaturesByUserId = ${JSON.stringify(userSignatures)};`,
        customToolsUri,
    ],
    tools: {
        ...getUserSignatureToolConfig({ users }),
    },
});

export const createEmailOptions = ({
    i18nEnabled,
    mergeFields,
    campaignLinks,
    bookingLinkHostname,
    appointmentTypes,
    users,
    userSignatures,
    customToolsUri,
    tags,
    files,
    environment,
    fileDownloadHyperlinksEnabled,
    appointmentToolEnabled,
    unlayerAiToolsEnabled,
    unlayerSmartMergeTagsEnabled,
    unlayerEmailBrandingCenterEnabled,
    brandPalette,
    userSignatureMergeFieldEnabled,
}) => merge(createBaseOptions({
            i18nEnabled, i18n, mergeFields, bookingLinkHostname, appointmentTypes,
        }),
        {
            mergeTagsConfig: {
                sort: false,
            },
            translations: {
                en: {
                    'labels.merge_tags': i18n.mergeFieldDropdownLabel,
                    'TagsMoreThanTen.title': 'Exceeded Tags',
                    'TagsMoreThanTen.description': 'You\'ve exceeded the maximum number of tags allowed. Please select up to 10 tags.',
                    'NoTagsAllowed.title': 'No tags allowed',
                    'NoTagsAllowed.description': 'Tags are not allowed on this block',
                    'LegacyLink.title': 'Missing Links',
                    'LegacyLink.description': 'Legacy links are not supported. Please update your link.',
                    'BlockError.title': 'Block Error',
                    'BlockError.description': 'An error occured while importing your block. Please check and edit your block.',
                    ...(unlayerEmailBrandingCenterEnabled ? {'editor.color.common_colors': 'Brand colors'} : {}),
                },
            },
            features: {
                ai: unlayerAiToolsEnabled ? {
                    smartText: true,
                    smartButtons: true,
                    magicImage: false,
                    smartImageAltText: false,
                    smartHeadings: true,
                } : false,
                smartMergeTags: unlayerSmartMergeTagsEnabled,
                preheaderText: false,
                textEditor: {
                    emojis: true,
                    ...(unlayerEmailBrandingCenterEnabled ? { inlineColorGroups: ['common_colors'] } : {}),
                },
                ...(unlayerEmailBrandingCenterEnabled ? {
                    colorPicker: {
                        colors: [{
                            id: 'common_colors',
                            colors: brandPalette,
                            default: true,
                        }],
                    },
                } : {}),
                audit: true,
                userUploads: { enabled: true, search: true },
            },
            customJS: [
                `window.signaturesByUserId = ${JSON.stringify(userSignatures)};`,
                customToolsUri,
                ...getEmailLinkTypes({
                    tags,
                    campaignLinks,
                    files,
                    fileDownloadHyperlinksEnabled,
                }),
                appointmentToolEnabled
                    ? 'unlayer.registerTool(keapUnlayerTools.KeapEmailAppointmentTool())'
                    : '',
                `unlayer.registerTool(keapUnlayerTools.KeapEmailFileDownloadTool(${fileDownloadHyperlinksEnabled}))`, // disable file download custom tool if file download hyperlinks feature is enabled.
                ...(unlayerEmailBrandingCenterEnabled ? [
                    'unlayer.registerTool(keapUnlayerTools.KeapLogoTool())',
                    'unlayer.registerPropertyEditor(keapUnlayerTools.KeapLogoEditor)',
                ] : []),
            ],
            safeHtml: {
                // All DOMPurify options can be passed into this configuration object.
                // In the future, it might be a good idea to also block certain event
                // attributes, and possibly hook into events to notify the user when
                // things are being sanitized from their HTML.
                // https://github.com/cure53/DOMPurify
                domPurifyOptions: {
                    FORBID_TAGS: [
                        '!doctype',
                        'audio',
                        'body',
                        'button',
                        'embed',
                        'form',
                        'head',
                        'html',
                        'iframe',
                        'input',
                        'link',
                        'noscript',
                        'object',
                        'option',
                        'optgroup',
                        'param',
                        'script',
                        'select',
                        'source',
                        'textarea',
                        'title',
                        'video',
                    ],
                },
            },
            tools: {
                ...getUserSignatureToolConfig({users, userSignatureMergeFieldEnabled}),
                ...getFileDownloadToolConfig(),
                ...getAppointmentToolConfig({appointmentToolEnabled, environment, appointmentTypes}),
            },
        });

const getFileDownloadToolConfig = () => ({
    'custom#unlayerFileDownloadTool': {
        properties: {
            fileDownloadEditor: {
                editor: {
                    data: {
                        messageChannelHost: window.origin,
                    },
                },
            },
        },
    },
});

const getUserSignatureToolConfig = ({ users, userSignatureMergeFieldEnabled }) => ({
    'custom#userSignature': {
        data: {
            userSignatureMergeFieldEnabled,
        },
        properties: {
            userId: {
                editor: {
                    data: {
                        options: [
                            { label: i18n.contactOwnerLabel, value: '' },
                            ...users.map(({ id, fullName }) => ({
                                label: fullName,
                                value: `${id}`,
                            })),
                        ],
                    },
                },
            },
        },
    },
});

const getAppointmentToolConfig = ({ appointmentToolEnabled, environment, appointmentTypes }) => {
    if (!appointmentTypes) {
        appointmentTypes = [];
    }

    const config = {
        'custom#unlayerAppointmentTool': {
            ...(!appointmentTypes?.length ? { usageLimit: 0 } : {}),
            data: {
                environment,
            },
            properties: {
                appointmentAction: {
                    editor: {
                        data: {
                            options: [
                                { label: 'Choose a link', value: '' },
                                ...appointmentTypes.map(({ name, bookingLink }) => ({
                                    label: name,
                                    value: bookingLink,
                                })),
                            ],
                        },
                    },
                },
            },
        },
    };

    return appointmentToolEnabled
        ? config
        : {};
};

export const createBlankUnlayerTemplate = () => ({
    counters: {
        u_column: 1,
        u_row: 1,
    },
    body: {
        rows: [
            {
                cells: [
                    1,
                ],
                columns: [
                    {
                        contents: [],
                        values: {
                            backgroundColor: '',
                            padding: '0px',
                            border: {},
                            _meta: {
                                htmlID: 'u_column_1',
                                htmlClassNames: 'u_column',
                            },
                        },
                    },
                ],
                values: {
                    displayCondition: null,
                    columns: false,
                    backgroundColor: '',
                    columnsBackgroundColor: '',
                    backgroundImage: {
                        url: '',
                        fullWidth: true,
                        repeat: false,
                        center: true,
                        cover: false,
                    },
                    padding: '0px',
                    _meta: {
                        htmlID: 'u_row_1',
                        htmlClassNames: 'u_row',
                    },
                    selectable: true,
                    draggable: true,
                    duplicatable: true,
                    deletable: true,
                    hideable: true,
                },
            },
        ],
        values: {
            backgroundColor: '#FFFFFF',
            backgroundImage: {
                url: '',
                fullWidth: true,
                repeat: false,
                center: true,
                cover: false,
            },
            contentWidth: '500px',
            contentAlign: 'center',
            fontFamily: {
                label: 'Arial',
                value: 'arial,helvetica,sans-serif',
            },
            linkStyle: {
                body: true,
                linkColor: '#0000ee',
                linkHoverColor: '#0000ee',
                linkUnderline: true,
                linkHoverUnderline: true,
            },
            _meta: {
                htmlID: 'u_body',
                htmlClassNames: 'u_body',
            },
        },
    },
    schemaVersion: 5,
});

export const createSimpleTextUnlayerTemplate = () => ({
    counters: {
        u_column: 1,
        u_row: 1,
        u_content_custom_canSpamFooter: 1,
        u_content_custom_userSignature: 1,
        u_content_text: 1,
    },
    body: {
        rows: [
            {
                cells: [
                    1,
                ],
                columns: [
                    {
                        contents: [
                            {
                                type: 'text',
                                values: {
                                    containerPadding: '10px',
                                    textAlign: 'left',
                                    lineHeight: '140%',
                                    linkStyle: {
                                        inherit: true,
                                        linkColor: '#0000ee',
                                        linkHoverColor: '#0000ee',
                                        linkUnderline: true,
                                        linkHoverUnderline: true,
                                    },
                                    _meta: {
                                        htmlID: 'u_content_text_1',
                                        htmlClassNames: 'u_content_text',
                                    },
                                    selectable: true,
                                    draggable: true,
                                    duplicatable: true,
                                    deletable: true,
                                    hideable: true,
                                    text: '<p style="font-size: 14px; line-height: 140%;">&nbsp;</p>\n<p style="font-size: 14px; line-height: 140%;">This is a new Text block. Change the text.</p>\n<p style="font-size: 14px; line-height: 140%;">You are not restricted to using other controls here.</p>\n<p style="font-size: 14px; line-height: 140%;">&nbsp;</p>',
                                },
                            },
                            {
                                type: 'custom',
                                slug: 'userSignature',
                                values: {
                                    userId: '',
                                    containerPadding: '10px',
                                    _meta: {
                                        htmlID: 'u_content_custom_userSignature_1',
                                        htmlClassNames: 'u_content_custom_userSignature',
                                    },
                                    selectable: true,
                                    draggable: true,
                                    duplicatable: true,
                                    deletable: true,
                                    hideable: true,
                                },
                            },
                        ],
                        values: {
                            backgroundColor: '',
                            padding: '0px',
                            border: {},
                            _meta: {
                                htmlID: 'u_column_1',
                                htmlClassNames: 'u_column',
                            },
                        },
                    },
                ],
                values: {
                    displayCondition: null,
                    columns: false,
                    backgroundColor: '',
                    columnsBackgroundColor: '',
                    backgroundImage: {
                        url: '',
                        fullWidth: true,
                        repeat: false,
                        center: true,
                        cover: false,
                    },
                    padding: '0px',
                    hideDesktop: false,
                    _meta: {
                        htmlID: 'u_row_1',
                        htmlClassNames: 'u_row',
                    },
                    selectable: true,
                    draggable: true,
                    duplicatable: true,
                    deletable: true,
                    hideable: true,
                },
            },
        ],
        values: {
            textColor: '#000000',
            backgroundColor: '#FFFFFF',
            backgroundImage: {
                url: '',
                fullWidth: true,
                repeat: false,
                center: true,
                cover: false,
            },
            contentWidth: '500px',
            contentAlign: 'left',
            fontFamily: {
                label: 'Arial',
                value: 'arial,helvetica,sans-serif',
            },
            linkStyle: {
                body: true,
                linkColor: '#0000ee',
                linkHoverColor: '#0000ee',
                linkUnderline: true,
                linkHoverUnderline: true,
            },
            _meta: {
                htmlID: 'u_body',
                htmlClassNames: 'u_body',
            },
        },
    },
    schemaVersion: 6,
});

export const createNullTemplateForAutomationBuilder = () => {
    return {
        body: {
            rows: [],
            values: {},
        },
        schemaVersion: 0,
    };
};

export const appendOptOutFooter = (design) => {
    design = design ?? createBlankUnlayerTemplate();

    const { rows } = design.body;

    const hasOptOutFooter = rows.some((row) => {
        return row.columns[0]?.contents[0]?.slug === 'canSpamFooter';
    });

    // If the design's last row already has a "canSpamFooter" instance,
    // then we don't need to add another one.
    if (!hasOptOutFooter) {
        design.body.rows = [...rows, createOptOutFooterRow()];

        // Update counters metadata, because Unlayer...
        design.counters.u_column += 1;
        design.counters.u_row += 1;
        design.counters.u_content_custom_canSpamFooter = 1;
    }

    return design;
};

export const createOptOutFooterRow = () => ({
    cells: [
        1,
    ],
    columns: [
        {
            contents: [
                {
                    type: 'custom',
                    slug: 'canSpamFooter',
                    values: {
                        additionalText: '',
                        linkPreText: '',
                        linkPostText: '',
                        alignment: 'center',
                        containerPadding: '10px',
                        _meta: {
                            htmlID: 'u_content_custom_canSpamFooter_1',
                            htmlClassNames: 'u_content_custom_canSpamFooter',
                        },
                        selectable: true,
                        draggable: false,
                        duplicatable: false,
                        deletable: false,
                        hideable: false,
                    },
                },
            ],
            values: {
                backgroundColor: '',
                padding: '0px',
                border: {},
                _meta: {
                    htmlID: 'u_column_1',
                    htmlClassNames: 'u_column',
                },
            },
        },
    ],
    values: {
        displayCondition: null,
        columns: false,
        backgroundColor: '#FFFFFF',
        columnsBackgroundColor: '',
        backgroundImage: {
            url: '',
            fullWidth: true,
            repeat: false,
            center: true,
            cover: false,
        },
        padding: '0px',
        hideDesktop: false,
        _meta: {
            htmlID: 'u_row_1',
            htmlClassNames: 'u_row',
        },
        selectable: false,
        draggable: false,
        duplicatable: false,
        deletable: false,
        hideable: false,
    },
});

/*
    Errors/warnings generated during bard to unlayer template migration
    are stored in the design json. These are extracted from the json
    and displayed in the audit tab. As of this commit, only these errors/warnings are
    dismissable. If an audit item is dismissed then that info is stored
    in the design itself.

    If this info (audit items + when they are dismissed) is left in the design
    json itself it would not do anything. But it's bloat. This wouldn't really
    cause any performance issues too because this info does not take up much
    space either, I think 1 KB (rough estimate) would be max for a template.
    For comparison, one of the default templates has design json that's about 40+ KB.
    But it would be better to remove this info from the design even if the real world
    impact on performance is very little to negligible.
*/
export function removeDismissedAuditItems(design) {
    if (design == null) return;

    const { rows } = design.body;

    try {
        for (const row of rows) {
            for (const column of row.columns) {
                for (const content of column.contents) {
                    // eslint-disable-next-line no-continue
                    if (!Array.isArray(content.values.audits)) continue;

                    const dimissedItems = content.values.audits.map(({ id }) => id);
                    // eslint-disable-next-line no-underscore-dangle
                    const errors = content.values._meta.keapCustomErrors.filter(({ id }) => !dimissedItems.includes(id));

                    if (errors.length === 0) {
                        // eslint-disable-next-line no-underscore-dangle
                        delete content.values._meta.keapCustomErrors;
                    } else {
                        // eslint-disable-next-line no-underscore-dangle
                        content.values._meta.keapCustomErrors = errors;
                    }

                    delete content.values.audits;
                }
            }
        }
    } catch (e) {
        // ignore
    }
}

export function convertCampaignLinkIdsToString(design) {
    if (design !== null) {
        /*
        convert campaign link IDs from number to string type

        Design's body contains rows that can include a number of columns
        And each column can contain an array of contents
        Also Columns CANNOT contain columns inside them
        Each item in the contents array represents some type of content like a button, image, text etc.
        For content items like button if a campaign link is present it is present @ content.values.href.values.campaignlink
        For content items like images, timers if a campaign link is present it is present @ content.values.action.values.campaignlink
        As of this commit only content types of button, image, heading, text, timer can have links so only these can contain campaign links but even if another tool was introduced in the future this code will most likely be able to cover it because unlayer seems to only be including links in the design json in two place and they are covered below.
        For content items of type text, heading the design json contains the raw html where campaign link is stored in an attribute data-campaign-link-id. so it's already a string. But it's also stored in data-u-link-value which is a base64 encoded serialized json which contains the campaign link ID as an int
        */

        const { rows } = design.body;

        rows.forEach((row) => {
            const { columns } = row;

            columns.forEach((column) => {
                const { contents } = column;

                contents.forEach((content) => {
                    if (typeof content.values?.href?.values?.campaignlink === 'number') {
                        content.values.href.values.campaignlink = content.values.href.values.campaignlink.toString();
                    }

                    if (typeof content.values?.action?.values?.campaignlink === 'number') {
                        content.values.action.values.campaignlink = content.values.action.values.campaignlink.toString();
                    }

                    if (typeof content.values?.text === 'string') {
                        const container = document.createElement('div');

                        container.innerHTML = content.values.text;

                        const elements = container.querySelectorAll('*');

                        elements.forEach((el) => {
                            const attrVal = el.getAttribute('data-u-link-value');

                            if (attrVal !== '') {
                                try {
                                    const json = JSON.parse(atob(attrVal)); // Better be careful

                                    json.values.campaignlink = json.values.campaignlink.toString();
                                    el.setAttribute('data-u-link-value', btoa(JSON.stringify(json)));
                                } catch (e) {
                                    // ignore
                                }
                            }
                        });
                        content.values.text = container.innerHTML;
                    }
                });
            });
        });
    }
}

export function replaceCampaignLinkIdsWithKeys(design, campaignLinks) {
    const links = campaignLinks;
    const { rows } = design.body;

    rows.forEach((row) => {
        const { columns } = row;

        columns.forEach((column) => {
            const { contents } = column;

            contents.forEach((content) => {
                if (typeof content.values?.href?.values?.campaignlink === 'number' || typeof content.values?.href?.values?.campaignlink === 'string') {
                    const funnelLink = links.find((link) => {
                        return link.id === content.values.href.values.campaignlink || link.id.toString() === content.values.href.values.campaignlink.toString();
                    });

                    content.values.href.values.campaignlink = funnelLink.linkKey;
                }

                if (typeof content.values?.action?.values?.campaignlink === 'number' || typeof content.values?.action?.values?.campaignlink === 'string') {
                    const funnelLink = links.find((link) => {
                        return link.id === content.values.action.values.campaignlink || link.id.toString() === content.values.action.values.campaignlink.toString();
                    });

                    content.values.action.values.campaignlink = funnelLink.linkKey;
                }

                if (typeof content.values?.text === 'string') {
                    const container = document.createElement('div');

                    container.innerHTML = content.values.text;

                    const elements = container.querySelectorAll('*');

                    elements.forEach((el) => {
                        const attrVal = el.getAttribute('data-u-link-value');
                        const dataCampaignLinkIdAt = el.getAttribute('data-campaign-link-id');
                        const hrefAttr = el.getAttribute('href');

                        if (dataCampaignLinkIdAt !== '' && dataCampaignLinkIdAt !== null) {
                            const funnelLink = links.find((link) => {
                                return link.id === dataCampaignLinkIdAt || link.id.toString() === dataCampaignLinkIdAt.toString();
                            });

                            el.setAttribute('data-campaign-link-id', funnelLink.linkKey);
                        }

                        if (hrefAttr !== '' && hrefAttr !== null) {
                            const funnelLink = links.find((link) => {
                                return link.id === dataCampaignLinkIdAt || link.id.toString() === dataCampaignLinkIdAt.toString();
                            });

                            el.setAttribute('href', funnelLink.linkKey);
                        }

                        if (attrVal !== '') {
                            try {
                                const json = JSON.parse(atob(attrVal)); // Better be careful
                                const funnelLink = links.find((link) => {
                                    return link.id === json.values.campaignlink || link.id.toString() === json.values.campaignlink.toString();
                                });

                                json.values.campaignlink = funnelLink.linkKey;
                                el.setAttribute('data-u-link-value', btoa(JSON.stringify(json)));
                            } catch (e) {
                                // ignore
                            }
                        }
                    });
                    content.values.text = container.innerHTML;
                }
            });
        });
    });
}

export function replaceCampaignMergeFieldIdsWithKeys(stringToReplace, funnelMergeFields) {
    if (!stringToReplace || stringToReplace === '') {
        return stringToReplace;
    }

    let replacedString = stringToReplace;

    funnelMergeFields.forEach((funnelMergeField) => {
        const funnelMergeFieldToReplace = `Campaign.${funnelMergeField.label}_${funnelMergeField.id}`;
        const replacement = `Campaign.${funnelMergeField.label}_${funnelMergeField.mergeKey}`;

        replacedString = replacedString.replaceAll(funnelMergeFieldToReplace, replacement);
    });

    return replacedString;
}

export const mergeCategoryWithTag = ({ catName, tagLabel, value }) => {
    let label;

    if (catName) {
        label = `${catName} -> ${tagLabel}`;
    } else {
        label = tagLabel;
    }

    return {
        label,
        value,
    };
};

export const mergeCategoryAndTagName = (tags) => {
    return tags.map((tag) => {
        return mergeCategoryWithTag({
            catName: tag.category?.name,
            tagLabel: tag.label,
            value: tag.value,
        });
    });
};

export const containsCampaignLinksWithId = (html) => {
    return html.includes('campaign-link-id') && !html.includes('campaign-link-id="K');
};

// similar to `containsCampaignMergeFieldWithId` but the other
// function does not return true if the input has some merge fields in
// new format (that use merge key) and some merge fields in old format (that use ID).
// But this function only checks if there are campaign merge fields that uses ID
// and if so it returns true
export const containsAnyCampaignMergeFieldWithId = (str) => {
    const mergeFieldMatches = str.matchAll(/~Campaign\..*_\d+~/g);

    return Array.from(mergeFieldMatches).length > 0;
};

export const containsCampaignMergeFieldsWithId = (html) => {
    // eslint-disable-next-line
    const mergeKeyFound = html.match('Campaign\..*_K');
    // eslint-disable-next-line
    const mergeFieldFound = html.match('Campaign\..*_');

    return Boolean((mergeFieldFound !== null) && (mergeKeyFound === null));
};

export const getEmailLinkTypes = ({
    tags,
    campaignLinks,
    isMultipleTagsEnabled = true,
    isTagCreationEnabled = true,
    fileDownloadHyperlinksEnabled = false,
    files,
}) => {
    if (!Array.isArray(tags)) return [];

    const isCampaignLinksEnabled = Array.isArray(campaignLinks) && campaignLinks.length > 0;

    const options = {
        i18n,
        tags,
        isCampaignLinksEnabled,
        isFileDownloadLinksEnabled: fileDownloadHyperlinksEnabled,
        campaignLinks,
        isMultipleTagsEnabled,
        tagLimit: UNLAYER_EMAIL_LINKS_TAG_LIMIT,
        isTagCreationEnabled,
        files,
        origin: window.origin,
    };

    return [`keapUnlayerTools.setEmailLinkTypesWithOptions(${JSON.stringify(options)})`];
};

export const createNewUnlayerEmail = (source = SOURCES.BROADCAST, toAddressEnabled) => {
    const email = {
        source,
        id: null,
        templateId: null,
        templateTitle: '',
        [STEPS.SENDER]: {
            from: DEFAULT_FROM_VALUE,
            replyToAddress: '',
        },
        [STEPS.SUBJECT]: {
            subject: '',
            previewText: '',
        },
        [STEPS.CONTENT]: {
            design: {},
            htmlBody: '<html></html>',
        },
    };

    if (source === SOURCES.BROADCAST) {
        email[STEPS.SCHEDULE] = {
            scheduleDate: null,
            useContactTimeZone: true,
            timeZoneId: '',
        };

        if (!toAddressEnabled) {
            email[STEPS.AUDIENCE] = {
                recipients: [],
            };
        } else {
            email[STEPS.AUDIENCE] = {
                recipients: [],
                toAddress: DEFAULT_TOADDRESS_VALUE,
            };
        }
    } else { // automations
        email[STEPS.AUDIENCE] = {
            toAddress: '',
        };

        email[STEPS.CONTENT].design = createNullTemplateForAutomationBuilder();
    }

    return email;
};

export function validateUnlayerEmail(email, step, options = {}) {
    const validators = {
        [STEPS.SUBJECT]() {
            const subjectLength = (email[STEPS.SUBJECT].subject?.trim() ?? '').length;
            const previewLength = (email[STEPS.SUBJECT].previewText ?? '').length;

            if (!options.enforceSubjectMaxLimit) {
                return subjectLength > 0;
            }

            return subjectLength > 0
                && subjectLength <= EMAIL_BROADCAST_SUBJECT.maxlength
                && previewLength <= EMAIL_BROADCAST_PREVIEW.maxlength;
        },

        [STEPS.CONTENT]() {
            const searializedDesign = JSON.stringify(email[STEPS.CONTENT].design);

            if (searializedDesign === JSON.stringify(createNullTemplateForAutomationBuilder())) {
                return false;
            }

            if (searializedDesign === JSON.stringify({})) {
                return false;
            }

            return true;
        },
    };

    if (validators[step]) {
        return validators[step]();
    }

    return false;
}
